<template>
  <success v-if="openSuccess" @closeModal="handleCloseSuccess" />
    <PlatformLayout :layout-class="programLayoutClass" :title="newData?.programName || 'Программа'">
    <div class="pd" :class="{ 'pd--curator': $store.state.role === 'curator' || $store.state.role === 'expert' }">

      <div v-if="pageLoading" class="pd-sk" aria-busy="true" aria-label="Загрузка программы">
        <div class="sk sk--hero" />
        <div class="sk sk--thumb-row" />
        <div class="sk sk--title" />
        <div class="sk sk--text" />
        <div class="sk sk--text" style="width: 80%" />
        <div class="pd-sk__body">
          <div v-for="n in 4" :key="n" class="sk sk--block" />
        </div>
      </div>

      <template v-else>
      <!-- ── Top action bar ─────────────────────────── -->
      <div class="pd__topbar">
        <button @click="goBack" class="pd__back">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Назад
        </button>
        <div class="pd__topbar-right" v-if="newData?.id">
          <button
            v-if="$store.state.role === 'user'"
            class="pd__action-btn"
            :class="{ 'pd__action-btn--active': newData.is_favourite == 1 }"
            @click="newData.is_favourite == 0 ? addToFav() : deleteFromFav()"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    :fill="newData.is_favourite == 1 ? '#e8192c' : 'none'" :stroke="newData.is_favourite == 1 ? '#e8192c' : 'currentColor'"/>
            </svg>
            {{ newData.is_favourite == 1 ? 'В избранном' : 'В избранное' }}
          </button>
          <a v-if="newData?.link" :href="newData.link" class="pd__action-btn pd__action-btn--primary" target="_blank">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            Сайт программы
          </a>
        </div>
      </div>

      <!-- ── Hero gallery (user / guest) ───────────── -->
      <div class="pd__hero" v-if="($store.state.role === 'user' || $store.state.role === 'guest') && newData?.images?.length">
        <div class="pd__hero-main">
          <img src="@/assets/icons/arrowRight.svg" class="pd-arrow pd-arrow--right" />
          <img src="@/assets/icons/arrowRight.svg" class="pd-arrow pd-arrow--left" style="rotate: 180deg;" />
          <Swiper
            v-if="thumbsSwiper"
            class="pd__hero-swiper"
            ref="topSwiper"
            :modules="modules"
            :thumbs="{ swiper: thumbsSwiper }"
            :navigation="{ nextEl: '.pd-arrow--right', prevEl: '.pd-arrow--left' }"
            :speed="400"
            :breakpoints="{ 0: { slidesPerView: 1, spaceBetween: 0 } }"
          >
            <SwiperSlide v-for="(item, idx) in newData.images" :key="idx" class="pd__hero-slide">
              <img :src="item.image" v-if="!item.isVideo" />
              <iframe v-if="item.isVideo" :src="item.videoUrl" frameborder="0" allowfullscreen width="100%" height="100%"></iframe>
            </SwiperSlide>
          </Swiper>
        </div>
        <div class="pd__hero-thumbs">
          <Swiper
            class="pd__thumbs-swiper"
            ref="botSwiper"
            :modules="modules"
            :speed="400"
            :breakpoints="{ 0: { slidesPerView: 4, spaceBetween: 10 } }"
            @swiper="setThumbsSwiper"
            @slideChange="setThumbsSwiper"
          >
            <SwiperSlide
              v-for="(item, idx) in newData.images" :key="idx"
              class="pd__thumb-slide"
              :class="{ islastactive: idx === newData.images.length - 1 && newData.video }"
              :style="{ backgroundImage: !item.isVideo ? `url(${item.image})` : `url(${item.backPhoto})` }"
            >
              <iframe v-if="item.isVideo" :src="item.videoUrl" frameborder="0" allowfullscreen width="100%" height="100%"></iframe>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <!-- ── Program header ─────────────────────────── -->
      <div class="pd__header">
        <div class="pd__badges">
          <span v-if="newData?.top" class="pd__badge" :class="(newData.top || '').toLowerCase() === 'elite' ? 'pd__badge--elite' : 'pd__badge--top'">
            {{ newData.top }}
          </span>
          <span v-if="newData?.subcategory?.category?.title" class="pd__badge pd__badge--gray">{{ newData.subcategory.category.title }}</span>
          <span v-if="newData?.country" class="pd__badge pd__badge--blue">{{ newData.country }}</span>
          <span v-if="newData?.is_student_choose" class="pd__badge pd__badge--gold">Выбор студентов</span>
        </div>
        <p class="pd__univ">{{ newData?.university?.title }}</p>
        <h1 class="pd__name">{{ newData?.programName }}</h1>
      </div>

      <!-- ── Description ─────────────────────────────── -->
      <div class="pd__desc-card">
        <h3 class="pd__section-title">Описание</h3>
        <p v-if="!newData?.description" class="pd__empty-text">Информация отсутствует</p>
        <template v-else>
          <p class="pd__desc-text" :class="{ 'pd__desc-text--expanded': descExpanded }">{{ convertHtmlToText(newData.description) }}</p>
          <button v-if="convertHtmlToText(newData.description).length > 400" class="pd__expand-btn" @click="descExpanded = !descExpanded">
            {{ descExpanded ? 'Свернуть' : 'Подробнее' }}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                 :style="{ transform: descExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </template>
      </div>

      <!-- ── Detailed info ───────────────────────────── -->
      <div class="pd__detail-block">
        <h2 class="pd__detail-block-title">Подробная информация</h2>

        <div v-if="deadlineInfo" class="pd__deadline">
          <div class="pd__deadline-icon">
            <img :src="require('@/assets/icons/img_10.svg')" />
          </div>
          <div class="pd__deadline-body">
            <span class="pd__deadline-label">Крайний срок подачи</span>
            <span class="pd__deadline-date">{{ deadlineInfo.primary }}</span>
            <span v-if="deadlineInfo.secondary" class="pd__deadline-hint">{{ deadlineInfo.secondary }}</span>
          </div>
        </div>

        <section v-if="admissionRequirements.length" class="pd__stat-section">
          <h3 class="pd__stat-section-title">Требования к поступлению</h3>
          <div class="pd__stats pd__stats--req">
            <div
              v-for="item in admissionRequirements"
              :key="item.label"
              class="pd__stat"
            >
              <div class="pd__stat-icon">
                <img :src="require('@/assets/icons/' + item.image + '.svg')" alt="" />
              </div>
              <span class="pd__stat-label">{{ item.label }}</span>
              <span
                class="pd__stat-value"
                :class="{ 'pd__stat-value--muted': item.value === '—' }"
              >{{ item.value }}</span>
            </div>
          </div>
        </section>

        <section v-if="locationDetails.length" class="pd__stat-section">
          <h3 class="pd__stat-section-title">Локация и программа</h3>
          <div class="pd__stats pd__stats--loc">
            <div
              v-for="item in locationDetails"
              :key="item.label"
              class="pd__stat"
              :class="{ 'pd__stat--clickable': isLocationStat(item) }"
              @click="handleLocationStatClick(item)"
            >
              <div class="pd__stat-icon">
                <img :src="require('@/assets/icons/' + item.image + '.svg')" alt="" />
              </div>
              <span class="pd__stat-label">{{ item.label }}</span>
              <span
                class="pd__stat-value"
                :class="{ 'pd__stat-value--muted': item.value === '—' }"
              >{{ item.value }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- ── Academic requirements + documents ─────── -->
      <div class="pd__areas-row">
        <div
          class="pd__area"
          :class="{ 'pd__area--expanded': expandedSections.details, 'pd__area--fits': !overflowingSections.details }"
        >
          <h3 class="pd__area-title">Академические требования</h3>
          <div v-if="newData?.details" class="pd__area-content">
            <div class="customTextArea" ref="detailsText" v-html="newData.details"></div>
          </div>
          <p v-else class="pd__empty-text">Академические требования не указаны</p>
          <button v-if="overflowingSections.details" class="pd__area-toggle" @click="expandedSections.details = !expandedSections.details">
            {{ expandedSections.details ? 'Свернуть' : 'Показать ещё' }}
            <span class="pd__toggle-arrow" :class="{ rotated: expandedSections.details }">↓</span>
          </button>
        </div>

        <div
          class="pd__area"
          :class="{ 'pd__area--expanded': expandedSections.docs, 'pd__area--fits': !overflowingSections.docs }"
        >
          <h3 class="pd__area-title">Перечень документов</h3>
          <div v-if="newData?.docs" class="pd__area-content">
            <div class="customTextArea" ref="docsText" v-html="newData.docs"></div>
          </div>
          <p v-else class="pd__empty-text">Перечень документов не указан</p>
          <button v-if="overflowingSections.docs" class="pd__area-toggle" @click="expandedSections.docs = !expandedSections.docs">
            {{ expandedSections.docs ? 'Свернуть' : 'Показать ещё' }}
            <span class="pd__toggle-arrow" :class="{ rotated: expandedSections.docs }">↓</span>
          </button>
        </div>
      </div>

      <div
        v-if="newData?.other"
        class="pd__area"
        :class="{ 'pd__area--expanded': expandedSections.other, 'pd__area--fits': !overflowingSections.other }"
      >
        <h3 class="pd__area-title">Другое</h3>
        <div class="pd__area-content">
          <div class="customTextArea" ref="otherText" v-html="newData.other"></div>
        </div>
        <button v-if="overflowingSections.other" class="pd__area-toggle" @click="expandedSections.other = !expandedSections.other">
          {{ expandedSections.other ? 'Свернуть' : 'Показать ещё' }}
          <span class="pd__toggle-arrow" :class="{ rotated: expandedSections.other }">↓</span>
        </button>
      </div>

      <div class="pd__map-card">
          <div class="pd__map-head">
            <h3 class="pd__section-title pd__section-title--map">
              <span class="pd__map-head-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </span>
              Местоположение
            </h3>
            <p v-if="mapLocationLabel" class="pd__map-subtitle">{{ mapLocationLabel }}</p>
          </div>

          <div v-if="hasMapCoords" class="pd__map-wrap">
            <div id="program-leaflet-map" class="pd__map-canvas"></div>
            <div v-if="mapLocationLabel" class="pd__map-chip">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
              </svg>
              {{ mapLocationLabel }}
            </div>
          </div>
          <div v-else class="pd__map-empty">
            <span class="pd__map-empty-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </span>
          <p>Координаты кампуса пока не указаны</p>
        </div>
      </div>

      <!-- ── Report outdated (user only) ───────────── -->
      <div v-if="$store.state.role === 'user'" class="pd__report">
        <div class="pd__report-header">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#11415D" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3 class="pd__report-title">Нашли устаревшую информацию?</h3>
        </div>
        <p class="pd__report-hint">Опишите, что именно устарело — мы проверим и обновим данные.</p>
        <textarea class="pd__report-textarea" v-model="comment" placeholder="Опишите устаревшую информацию…"></textarea>
        <div v-if="file" class="pd__file-chip">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          {{ file.name }}
          <button class="pd__file-chip-remove" @click="deleteFile()">×</button>
        </div>
        <div class="pd__report-actions">
          <label for="program_document" class="pd__upload-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Прикрепить файл
            <input type="file" id="program_document" style="display: none;" @change="handleFileChange" />
          </label>
          <button class="pd__send-btn" @click="sendSuccess()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Отправить
          </button>
        </div>
      </div>

      <!-- ── Similar programs ───────────────────────── -->
      <div v-if="$store.state.role === 'user' && similarPrograms.length" class="pd__similar">
        <h3 class="pd__section-title">Похожие программы</h3>
        <div class="pd__similar-grid">
          <ProgramCard v-for="item in similarPrograms.slice(0, 6)" :key="item.id" :item="item" @fav-changed="getPage" />
        </div>
      </div>

      </template>
    </div>
  </PlatformLayout>
</template>
<script>
import PlatformLayout from '@/components/platform/PlatformLayout.vue';
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import SwiperCore, { A11y, Autoplay, Lazy } from "swiper";
import { Navigation, Pagination } from 'swiper';
import { Thumbs, FreeMode, EffectFade } from "swiper";
import "swiper/swiper.min.css";
import success from '@/components/newPlatform/success.vue';
import ProgramCard from '@/components/programs/ProgramCard.vue';
SwiperCore.use([Navigation, Pagination, A11y, Autoplay, Lazy])
import axios from "axios";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import plusIcon from '@/assets/icons/video.jpg';
export default {
    components: {
        PlatformLayout, Swiper, SwiperSlide, ProgramCard, success
    },
    computed: {
        programLayoutClass() {
            const role = this.$store.state.role;
            if (role === 'expert' || role === 'curator') return 'mainStyle';
            if (role === 'supervisor' || role === 'chief') return 'mainStyle3';
            return 'mainStyle1';
        },
        admissionRequirements() {
            const greMap = { 1: 'Требуется', 2: 'Не требуется', 3: 'Рекомендуется' };
            const gpa = this.newData?.gpa_min;

            return [
                {
                    label: 'GPA (минимум)',
                    value: gpa != null && gpa !== '' ? Number(gpa).toFixed(2) : '—',
                    image: 'img_7',
                },
                {
                    label: 'IELTS',
                    value: this.newData?.ielts_value != null && this.newData.ielts_value !== ''
                        ? `${this.newData.ielts_value}`
                        : '—',
                    image: 'img_8',
                },
                {
                    label: 'GRE/GMAT',
                    value: greMap[this.newData?.gre] ?? '—',
                    image: 'img_9',
                },
                {
                    label: 'DUOLINGO',
                    value: this.formatDuolingoValue(),
                    image: 'img_13',
                },
            ];
        },
        locationDetails() {
            const durationMap = { 1: '1 год', 1.5: '1.5 года', 2: '2 года', 3: '3 года', 4: '4 года', 5: '5 лет' };

            return [
                { label: 'Страна', value: this.newData?.country || '—', image: 'img_5' },
                { label: 'Штат', value: this.newData?.state || '—', image: 'img_6' },
                { label: 'Город', value: this.newData?.city || '—', image: 'img_city' },
                { label: 'Длительность', value: durationMap[this.newData?.duration_value] ?? '—', image: 'img_3' },
                { label: 'Application fee', value: this.newData?.price || '—', image: 'img_4' },
            ];
        },
        hasMapCoords() {
            const lat = parseFloat(this.newData?.latitude);
            const lng = parseFloat(this.newData?.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        },
        mapLocationLabel() {
            const parts = [this.newData?.city, this.newData?.state, this.newData?.country]
                .filter((v) => v && v !== '—');
            return parts.join(', ');
        },
    },
    data() {
        return {
            pageLoading: true,
            thumbsSwiper: null,
            activeSlide: 0,
            modules: [Thumbs, FreeMode, EffectFade],
            openSuccess: false,
            file: '',
            comment: '',
            openMap: true,
            descExpanded: false,
            similarPrograms: [],
            newData: [],
            deadlineInfo: null,
            expandedSections: {
                details: true,
                docs: true,
                other: true
            },
            overflowingSections: {
                details: false,
                docs: false,
                other: false
            }
        }
    },
    methods: {
        formatDeadline(dateStr) {
            if (!dateStr) return '—'
            const [d, m] = dateStr.split('-')
            return new Date(`2000-${m}-${d}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
        },
        pluralRu(n, one, few, many) {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return one
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
            return many
        },
        buildDeadlineInfo() {
            const raw = this.newData?.autumn_admission_end_date
            const rolling = this.newData?.rolling_basis

            if (rolling) {
                return {
                    primary: 'Rolling basis',
                    secondary: 'Заявки принимаются круглый год'
                }
            }
            if (!raw) {
                return {
                    primary: '—',
                    secondary: 'Дата не указана'
                }
            }

            const [d, m, y] = raw.split('-')
            const target = new Date(`${y}-${m}-${d}T12:00:00`)
            const formatted = target.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

            const now = new Date()
            const diffMs = target - now
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

            let secondary = ''

            if (diffDays < 0) {
                secondary = ''
            } else if (diffDays === 0) {
                secondary = 'Сегодня последний день подачи'
            } else if (diffDays < 60) {
                secondary = `осталось ${diffDays} ${this.pluralRu(diffDays, 'день', 'дня', 'дней')}`
            } else {
                const months = Math.round(diffDays / 30)
                secondary = `осталось ${months} ${this.pluralRu(months, 'месяц', 'месяца', 'месяцев')}`
            }

            return { primary: formatted, secondary }
        },
        goBack() {
            this.$router.go(-1);
        },
        deleteFile(){
            this.file = '';
        },
        addToFav(){
            const data = {
                university_program_id: this.newData.id
            }
            axios.post('/platform/client/users/favourites', data , {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                }
            })
            .then((response) => {
            if (response.status === 201) {
                toast.success('Добавлено в избранное')
                this.getPage();
            } else {
                console.log(response.data);
            }
            })
            .catch((response) => { 
                console.log(response.response.data.errors);
            }); 
        },
        deleteFromFav(){
            axios.delete(`/platform/client/users/favourites/${this.newData.id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                }
            })
            .then((response) => {
            if (response.status === 202) {
                toast.success('Успешно удалено')
                this.getPage();
            } else {
                console.log(response.data);
            }
            })
            .catch((response) => { 
                console.log(response.response.data.errors);
            }); 
        },
        sendSuccess() {
            const formData = new FormData();
            if (!this.comment || this.comment.trim() === '') {
              toast.error('Пожалуйста, введите комментарий.');
              return;
            }
            formData.append('file', this.file);
            formData.append('text', this.comment);
            formData.append('university_program_id', this.newData.id);
            try {
                axios.post(`/platform/client/university-program-comment`, formData, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                });
                toast.success('Успешно отправлено!')
                this.comment ='';
                this.file = '';
            } catch (response) {
                toast.error(response.response.data.message);
            }
        },
        handleFileChange() {
            this.file = event.target.files[0];
        },
        convertHtmlToText(html) {
            if (!html) return '';

            return html
                .replace(/<br\s*\/?>/gi, '\n') // Заменяем <br> на перенос строки
                .replace(/<\/p>/gi, '\n\n') // Добавляем перенос строки после </p>
                .replace(/<[^>]*>/g, '') // Удаляем все HTML-теги
                .replace(/&nbsp;|&#160;/gi, ' ') // Меняем &nbsp; и его кодировку на пробел
                .replace(/&amp;/g, '&') // Декодируем &amp;
                .replace(/&lt;/g, '<') // Декодируем &lt;
                .replace(/&gt;/g, '>') // Декодируем &gt;
                .replace(/\s+/g, ' ') // Убираем лишние пробелы
                .trim(); // Очищаем пробелы по краям
        },
        setThumbsSwiper(swiper) {
            this.thumbsSwiper = swiper
        },
        async getPage() {
            this.pageLoading = true;
            await axios.get(`/V1/universityPrograms/${this.$route.params.id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                }
            })
                .then(res => {
                    this.newData = res.data.program
                    console.log('New Data:', this.newData)
                    this.similarPrograms = res.data.similarPrograms
                    this.updateProgramInfo();
                    this.newData.images = [
                        ...(this.newData.images || []),
                        { isVideo: true, videoUrl: this.newData.video, backPhoto: plusIcon }
                    ];
                    this.checkOverflows();
                })
                .catch(err => {
                })
                .finally(() => {
                    this.pageLoading = false;
                })
        },
        updateProgramInfo() {
            const lat = parseFloat(this.newData?.latitude);
            const lng = parseFloat(this.newData?.longitude);

            this.deadlineInfo = this.buildDeadlineInfo();

            if (this.hasMapCoords) {
                this.$nextTick(() => this.initProgramMap(lat, lng));
            } else if (this._leafletMap) {
                this.destroyProgramMap();
            }
        },
        createProgramMarkerIcon() {
            return L.divIcon({
                className: 'pd-map-marker-wrap',
                html: '<div class="pd-map-marker"><span class="pd-map-marker__dot"></span></div>',
                iconSize: [36, 44],
                iconAnchor: [18, 44],
            });
        },
        initProgramMap(lat, lng) {
            const mapEl = document.getElementById('program-leaflet-map');
            if (!mapEl) return;

            if (this._leafletMap) {
                this._leafletMap.setView([lat, lng], 14);
                this._leafletMap.invalidateSize();
                if (this._leafletMarker) this._leafletMarker.setLatLng([lat, lng]);
                return;
            }

            this._leafletMap = L.map(mapEl, {
                zoomControl: false,
                scrollWheelZoom: false,
                attributionControl: true,
            }).setView([lat, lng], 14);

            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
                subdomains: 'abcd',
                maxZoom: 20,
            }).addTo(this._leafletMap);

            L.control.zoom({ position: 'bottomright' }).addTo(this._leafletMap);

            this._leafletMarker = L.marker([lat, lng], {
                icon: this.createProgramMarkerIcon(),
            }).addTo(this._leafletMap);

            this.$nextTick(() => {
                this._leafletMap?.invalidateSize();
            });
        },
        destroyProgramMap() {
            if (this._leafletMap) {
                this._leafletMap.remove();
                this._leafletMap = null;
                this._leafletMarker = null;
            }
        },
        formatDuolingoValue() {
            if (this.newData?.duolingo_value != null && this.newData.duolingo_value !== '') {
                return `${this.newData.duolingo_value}`;
            }
            if (this.newData?.duolingo === 1 || this.newData?.duolingo === true) {
                return 'Да';
            }
            if (this.newData?.duolingo === 2 || this.newData?.duolingo === false) {
                return 'Нет';
            }
            return '—';
        },
        isLocationStat(item) {
            return ['Страна', 'Штат', 'Город'].includes(item?.label) && item?.value && item.value !== '—';
        },
        handleLocationStatClick(item) {
            if (!this.isLocationStat(item)) return;
            this.scrollToMap();
        },
        scrollToMap() {
            const el = document.getElementById('program-leaflet-map');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        checkOverflows() {
            const COLLAPSED_MAX = 240;
            const TOLERANCE = 8;
            this.$nextTick(() => {
                ['details', 'docs', 'other'].forEach(key => {
                    const el = this.$refs[`${key}Text`];
                    if (!el) {
                        this.overflowingSections[key] = false;
                        return;
                    }
                    this.overflowingSections[key] = el.scrollHeight > COLLAPSED_MAX + TOLERANCE;
                });
            });
        },
        handleCloseSuccess() {
            if (this.openSuccess) {
                this.openSuccess = false;
            }
        },
    },
    async created() {
        this.getPage();
    },
    mounted() {
        this._onResize = () => this.checkOverflows();
        window.addEventListener('resize', this._onResize);
    },
    beforeUnmount() {
        this.destroyProgramMap();
        if (this._onResize) {
            window.removeEventListener('resize', this._onResize);
        }
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.getPage();
            }
        },
    }
}
</script>
<style lang="scss" scoped>
/* ─────────────────────────────────────────────────────────
   Program Detail  –  pd namespace
   ───────────────────────────────────────────────────────── */

.pd {
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #111827;
}

/* ── top action bar ──────────────────────── */
.pd__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.pd__topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pd__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #374151;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover {
    border-color: #11415D;
    color: #11415D;
    background: rgba(17, 65, 93, 0.04);
  }
}

.pd__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  &--active {
    border-color: #e8192c;
    background: rgba(232, 25, 44, 0.06);
    color: #e8192c;
    &:hover { background: rgba(232, 25, 44, 0.1); }
  }

  &--primary {
    background: #0f1f3d;
    border-color: #0f1f3d;
    color: #fff;
    &:hover { background: #1a3560; border-color: #1a3560; }
  }
}

/* ── hero gallery ────────────────────────── */
.pd__hero {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pd__hero-main {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  background: #f0f1f3;
}

.pd__hero-swiper {
  width: 100%;
  aspect-ratio: 16 / 7;
}

.pd__hero-slide {
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.pd-arrow {
  position: absolute;
  width: 40px;
  height: 28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  cursor: pointer;
  user-select: none;

  &--left  { left: 12px; }
  &--right { right: 12px; }
}

.pd__hero-thumbs { width: 100%; }

.pd__thumbs-swiper { width: 100%; }

.pd__thumb-slide {
  aspect-ratio: 16/9;
  border-radius: 10px;
  background-position: center;
  background-size: cover;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.15s;
  position: relative;
  overflow: hidden;

  iframe { border-radius: 10px; pointer-events: none; }

  &.swiper-slide-thumb-active {
    border-color: #11415D;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(17, 65, 93, 0.35);
      background-image: url("@/assets/Eye.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-size: 30%;
      border-radius: 8px;
    }
  }
}

/* ── program header ──────────────────────── */
.pd__header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pd__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pd__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  &--top   { background: rgba(232, 25, 44, 0.1);  color: #e8192c; }
  &--elite { background: linear-gradient(135deg, #d4af37, #f5cc5b); color: #5a3e00; }
  &--gray  { background: #f3f4f6; color: #374151; }
  &--blue  { background: #eff6ff; color: #1d4ed8; }
  &--gold  { background: rgba(245, 158, 11, 0.12); color: #92400e; }
}

.pd__univ {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.02em;
}

.pd__name {
  margin: 0;
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

/* ── detailed info block ─────────────────── */
.pd__detail-block {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pd__detail-block-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.01em;
}

.pd__stat-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pd__stat-section-title {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* ── stat cards grid ─────────────────────── */
.pd__stats {
  display: grid;
  gap: 12px;

  &--req {
    grid-template-columns: repeat(4, 1fr);

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  &--loc {
    grid-template-columns: repeat(5, 1fr);

    @media (max-width: 1100px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 400px) {
      grid-template-columns: 1fr;
    }
  }
}

.pd__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 18px 12px 16px;
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.05);
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;

  &--clickable {
    cursor: pointer;

    &:hover {
      border-color: #c5d0d8;
      background: rgba(17, 65, 93, 0.03);
      box-shadow: 0 4px 14px rgba(17, 65, 93, 0.08);
    }
  }
}

.pd__stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(17, 65, 93, 0.07), rgba(36, 136, 195, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 20px;
    height: 20px;
    opacity: 0.85;
  }
}

.pd__stat-label {
  font-size: 10.5px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.3;
}

.pd__stat-value {
  font-size: 16px;
  font-weight: 800;
  color: #11415d;
  line-height: 1.2;

  &--muted {
    color: rgba(0, 0, 0, 0.3);
    font-weight: 500;
    font-size: 14px;
  }
}

.pd__areas-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

/* ── deadline banner ─────────────────────── */
.pd__deadline {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 22px 28px;
  background: linear-gradient(135deg, #0f1f3d 0%, #1a3a6b 100%);
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(15, 31, 61, 0.2);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -40%;
    right: -8%;
    width: 260px;
    height: 260px;
    background: radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 640px) { padding: 18px 20px; gap: 14px; }
}

.pd__deadline-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img { width: 26px; height: 26px; filter: brightness(0) invert(1); opacity: 0.9; }

  @media (max-width: 640px) { width: 42px; height: 42px; img { width: 20px; height: 20px; } }
}

.pd__deadline-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  z-index: 1;
}

.pd__deadline-label {
  font-size: 10.5px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.pd__deadline-date {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;

  @media (max-width: 640px) { font-size: 20px; }
}

.pd__deadline-hint {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
}

/* ── description card ────────────────────── */
.pd__desc-card,
.pd__map-card {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.04);

  @media (max-width: 640px) { padding: 20px; }
}

.pd__section-title {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.pd__desc-text {
  margin: 0;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &--expanded {
    display: block;
    -webkit-line-clamp: unset;
  }
}

.pd__expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: #11415D;
  cursor: pointer;
  transition: color 0.15s;

  &:hover { color: #2488C3; }
}

.pd__empty-text {
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

/* map card */
.pd__map-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.pd__section-title--map {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
}

.pd__map-head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(17, 65, 93, 0.08);
  color: #11415D;
  flex-shrink: 0;
}

.pd__map-subtitle {
  margin: 0 0 0 44px;
  font-size: 13.5px;
  color: #6b7280;
  line-height: 1.4;

  @media (max-width: 640px) {
    margin-left: 0;
  }
}

.pd__map-wrap {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8eaed;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(17, 65, 93, 0.06);
  background: #f4f6f8;
}

.pd__map-canvas {
  width: 100%;
  height: 340px;
  z-index: 0;
}

.pd__map-chip {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: calc(100% - 88px);
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.1);
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  pointer-events: none;

  svg {
    flex-shrink: 0;
    color: #e8192c;
  }
}

.pd__map-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 200px;
  padding: 32px 24px;
  border-radius: 14px;
  border: 1px dashed #d1d5db;
  background: linear-gradient(160deg, #fafbfc 0%, #f3f5f7 100%);
  text-align: center;

  p {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
  }
}

.pd__map-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fff;
  color: #9ca3af;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.06);
}

/* Leaflet overrides inside map */
.pd__map-wrap :deep(.leaflet-control-zoom) {
  border: none !important;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.12) !important;
  border-radius: 10px !important;
  overflow: hidden;
  margin: 0 12px 12px 0 !important;
}

.pd__map-wrap :deep(.leaflet-control-zoom a) {
  width: 32px !important;
  height: 32px !important;
  line-height: 32px !important;
  font-size: 16px !important;
  color: #374151 !important;
  border-color: #eef0f2 !important;
  background: rgba(255, 255, 255, 0.96) !important;

  &:hover {
    background: #fff !important;
    color: #11415D !important;
  }
}

.pd__map-wrap :deep(.leaflet-control-attribution) {
  font-size: 10px;
  background: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(4px);
  padding: 2px 8px !important;
  border-radius: 6px 0 0 0;
  color: #9ca3af !important;

  a {
    color: #6b7280 !important;
  }
}

.pd__map-wrap :deep(.pd-map-marker-wrap) {
  background: none !important;
  border: none !important;
}

.pd__map-wrap :deep(.pd-map-marker) {
  position: relative;
  width: 36px;
  height: 44px;
  filter: drop-shadow(0 4px 10px rgba(232, 25, 44, 0.35));
}

.pd__map-wrap :deep(.pd-map-marker::before) {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 28px;
  height: 28px;
  border-radius: 50% 50% 50% 0;
  background: linear-gradient(145deg, #ff3d4f 0%, #e8192c 55%, #c41424 100%);
  transform: translateX(-50%) rotate(-45deg);
}

.pd__map-wrap :deep(.pd-map-marker__dot) {
  position: absolute;
  left: 50%;
  top: 9px;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid rgba(255, 255, 255, 0.9);
  z-index: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* content area cards (requirements, docs) */
.pd__area {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;

  @media (max-width: 640px) { padding: 20px; }
}

.pd__area-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  padding-left: 14px;
  position: relative;
  line-height: 1.3;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 3px;
    bottom: 3px;
    width: 4px;
    border-radius: 2px;
    background: linear-gradient(180deg, #0f1f3d 0%, #2488C3 100%);
  }
}

.pd__area-content {
  position: relative;
  max-height: 240px;
  overflow: hidden;
  transition: max-height 0.35s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70px;
    background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, #fff 90%);
    pointer-events: none;
    transition: opacity 0.25s ease;
  }
}

.pd__area--expanded .pd__area-content {
  max-height: 4000px;
  &::after { opacity: 0; }
}

.pd__area--fits .pd__area-content {
  max-height: none;
  &::after { display: none; }
}

.pd__area-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: #11415D;
  cursor: pointer;
  padding: 4px 0;
  align-self: flex-start;
  transition: color 0.15s;

  &:hover { color: #2488C3; }
}

.pd__toggle-arrow {
  display: inline-block;
  transition: transform 0.25s ease;

  &.rotated { transform: rotate(180deg); }
}

/* rich text inside areas */
.customTextArea {
  color: rgba(0, 0, 0, 0.78);
  line-height: 1.65;

  :deep(p), :deep(span), :deep(li) {
    font-size: 15px !important;
    line-height: 1.7;
    color: rgba(0, 0, 0, 0.78) !important;
    margin-bottom: 8px;
  }

  :deep(strong) { color: #11415D; font-weight: 700; }

  :deep(ul), :deep(ol) { padding-left: 20px !important; margin: 4px 0 8px; }

  :deep(li) {
    padding-left: 4px;
    &::marker { color: #11415D; }
  }

  :deep(a) {
    color: #2488C3;
    text-decoration: none;
    border-bottom: 1px solid rgba(36, 136, 195, 0.3);
    transition: border-color 0.2s;
    &:hover { border-bottom-color: #2488C3; }
  }
}

/* ── report section ──────────────────────── */
.pd__report {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.04);

  @media (max-width: 640px) { padding: 20px; }
}

.pd__report-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pd__report-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.pd__report-hint {
  margin: 0;
  font-size: 13.5px;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.5;
}

.pd__report-textarea {
  width: 100%;
  height: 130px;
  padding: 14px 18px;
  border: 1.5px solid rgba(17, 65, 93, 0.1);
  border-radius: 12px;
  background: #f8f9fb;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  resize: none;
  outline: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

  &::placeholder { color: rgba(0, 0, 0, 0.3); }

  &:focus {
    border-color: #11415D;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(17, 65, 93, 0.06);
  }
}

.pd__file-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #f3f4f6;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  align-self: flex-start;
}

.pd__file-chip-remove {
  border: none;
  background: none;
  font-size: 16px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  &:hover { color: #e8192c; }
}

.pd__report-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.pd__upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: #11415D; background: rgba(17, 65, 93, 0.03); }
}

.pd__send-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  background: #0f1f3d;
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #1a3560; }
}

/* ── similar programs ────────────────────── */
.pd__similar {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pd__similar-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: 1fr; }
}

/* ── islastactive (video thumb) ──────────── */
.islastactive::before {
  background-image: url("@/assets/icons/video.svg") !important;
}

/* ── curator mode ────────────────────────── */
.pd--curator {
  background: #fff;
  border-radius: 14px;
  padding: 28px;
  border: 1px solid #e8eaed;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.05);

  @media (max-width: 640px) { padding: 16px; }
}

/* ── mobile tweaks ───────────────────────── */
@media (max-width: 768px) {
  .pd__topbar-right { flex-wrap: wrap; }
  .pd__deadline { flex-direction: row; }
  .pd__name { font-size: 20px; }
}
</style>